import { type HomeyAPI } from 'homey-api';
import { shallow } from 'zustand/shallow';

import { defer } from '../../../lib/defer';
import { PendingError } from '../../../lib/InternalError';

import { BaseApiStore, type FetchArgs } from '../../../store/BaseApiStore.ts';
import { selectBase } from '../../../store/selectors.ts';
import { useAttach } from '../../../store/useAttach';

type OwnerData = { [key: string]: Owner };

interface State {
  ownersData: null | OwnerData;
  isLoading: boolean;
  error: unknown;
}

export type NotificationSettingsStoreState = State;

type Owner = {
  enabled: boolean;
  push: boolean;
  size: number;
  uriObj: {
    name: string;
  };
};

class NotificationSettingsStore extends BaseApiStore<State> {
  constructor() {
    super('notification-owners-settings');
  }

  override createInitialState() {
    return {
      ownersData: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, cache, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    try {
      const ownersData = await api.notifications.getOwners();

      if (abortController.signal.aborted) {
        return;
      }

      this.set({
        ...this.createInitialState(),
        ownersData,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy() {}

  override onAttached({ api }: { api: HomeyAPI }): void {}

  override onDetached({ api }: { api: HomeyAPI }): void {}

  use() {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAttach(this, 'notification-owners-settings');

    return { notificationSettings: this.store(selectAll, shallow) };
  }

  async setOwnerEnabled({ uri, enabled }: { uri: string; enabled: boolean }) {
    const key = `${this.setOwnerEnabled.name}:${uri}`;

    if (this.pending[key] != null) {
      throw new PendingError();
    }

    this.pending[key] = defer();

    try {
      const api = this.getApi();
      const state = this.get();
      const prevData = state.ownersData?.[uri];

      if (prevData == null) {
        throw new Error('Trying to set owner enabled a non existing owner.');
      }

      this.set({
        ownersData: {
          ...state.ownersData,
          [uri]: {
            ...prevData,
            enabled: enabled,
          },
        },
      });

      try {
        await api.notifications.setOwnerEnabled({ uri, enabled });
      } catch (error) {
        this.set({
          ownersData: {
            ...state.ownersData,
            [uri]: {
              ...prevData,
              enabled: prevData.enabled,
            },
          },
        });
        throw error;
      }
    } finally {
      this.pending[key].resolve();
      delete this.pending[key];
    }
  }

  async setOwnerPush({ uri, push }: { uri: string; push: boolean }) {
    const key = `${this.setOwnerPush.name}:${uri}`;

    if (this.pending[key] != null) {
      throw new PendingError();
    }

    this.pending[key] = defer();

    try {
      const api = this.getApi();
      const state = this.get();
      const prevData = state.ownersData?.[uri];

      if (prevData == null) {
        throw new Error('Trying to set owner push a non existing owner.');
      }

      this.set({
        ownersData: {
          ...state.ownersData,
          [uri]: {
            ...prevData,
            push: push,
          },
        },
      });

      try {
        await api.notifications.setOwnerPush({ uri, push });
      } catch (error) {
        this.set({
          ownersData: {
            ...state.ownersData,
            [uri]: {
              ...prevData,
              push: prevData.push,
            },
          },
        });
        throw error;
      }
    } finally {
      this.pending[key].resolve();
      delete this.pending[key];
    }
  }

  async deleteNotifications({ uri }: { uri: string; push: boolean }) {
    const api = this.getApi();
    await api.notifications.deleteNotifications({ ownerUri: uri });
  }
}

function selectAll(state: NotificationSettingsStoreState) {
  return {
    ...selectBase(state),
    ownersData: state.ownersData,
  };
}

export const notificationSettingsStore = new NotificationSettingsStore();
